import React, {memo, useCallback} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C1A_CONTENT, C1aRow} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C1aAcceptanceViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    /** Template copy; defaults to code constant when omitted. */
    content?: typeof C1A_CONTENT;
}

const YES_NO_OPTIONS = ['', 'Yes', 'No', 'N/A'] as const;

const cellBorder = {borderColor: 'divider'};
/** Excel C1a: black column-header band under each section (PCL / AEL). */
const sectionColumnHeadSx = {
    ...cellBorder,
    bgcolor: '#000',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
    lineHeight: 1.25,
    borderColor: '#000',
    verticalAlign: 'middle' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

/** Column labels for the black header row under each C1a section. */
function C1aSectionColumnHeader({inquiryLabel}: {inquiryLabel: string}) {
    return (
        <TableRow>
            <TableCell sx={sectionColumnHeadSx} colSpan={2} align="center">
                {inquiryLabel}
            </TableCell>
            <TableCell sx={sectionColumnHeadSx} align="center">
                Yes/No
                <br/>
                or N/A
            </TableCell>
            <TableCell sx={sectionColumnHeadSx} align="center">
                Response
                <br/>
                (Comments)
            </TableCell>
            <TableCell sx={sectionColumnHeadSx} align="center">
                Initials
            </TableCell>
            <TableCell sx={sectionColumnHeadSx} align="center">
                W/P
                <br/>
                Ref.
            </TableCell>
        </TableRow>
    );
}

/**
 * Native control chrome matching InputField compact outlined style:
 * primary.main @ 6% fill, outlined border, body2 type, focus ring.
 */
const nativeFieldBaseSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 1,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
        borderColor: 'text.primary',
    },
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
    '&::placeholder': {
        color: 'text.disabled',
        opacity: 1,
    },
};

function NativeInput({
    value,
    onChange,
    align = 'left',
    placeholder,
    sx,
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center';
    placeholder?: string;
    sx?: SxProps<Theme>;
}) {
    return (
        <Box
            component="input"
            value={value}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={[
                nativeFieldBaseSx,
                {textAlign: align},
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ] as SxProps<Theme>}
        />
    );
}

function NativeSelect({
    value,
    onChange,
    children,
    align = 'left',
    sx,
}: {
    value: string;
    onChange: (v: string) => void;
    children: React.ReactNode;
    align?: 'left' | 'center';
    sx?: SxProps<Theme>;
}) {
    return (
        <Box
            component="select"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
            sx={[
                nativeFieldBaseSx,
                {
                    textAlign: align,
                    cursor: 'pointer',
                    // leave room for dropdown arrow like MUI Select
                    pr: 2.5,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    // Keep InputField tint when adding arrow (backgroundImage alone can wipe fill in some browsers)
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    backgroundImage: (theme) =>
                        `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
                            `<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'><path fill='${theme.palette.text.secondary}' d='M7 10l5 5 5-5z'/></svg>`,
                        )}")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ] as SxProps<Theme>}
        >
            {children}
        </Box>
    );
}

function NativeYesNo({value, onChange}: {value: string; onChange: (v: string) => void}) {
    return (
        <NativeSelect value={value} onChange={onChange} align="center">
            {YES_NO_OPTIONS.map((opt) => (
                <option key={opt || '_'} value={opt}>
                    {opt || '—'}
                </option>
            ))}
        </NativeSelect>
    );
}

function isInquiryRow(row: C1aRow): boolean {
    if (row.kind === 'heading') return false;
    if (row.inquiry === false) return false;
    return row.kind === 'main' || row.kind === 'sub';
}

function isResponseNote(row: C1aRow): boolean {
    if (row.kind !== 'note') return false;
    const t = row.text.toLowerCase();
    return (
        t.startsWith('additional') ||
        t.startsWith('assumptions') ||
        t.startsWith('if a threat') ||
        t.startsWith('if not') ||
        t.startsWith('findings')
    );
}

const InquiryRow = memo(function InquiryRow({
    row,
    yesNo,
    response,
    initials,
    wpRef,
    onChange,
}: {
    row: C1aRow;
    yesNo: string;
    response: string;
    initials: string;
    wpRef: string;
    onChange: (key: string, value: string) => void;
}) {
    const indent = row.kind === 'sub';
    const isNote = row.kind === 'note';
    const isHeading = row.kind === 'heading';
    const inquiry = isInquiryRow(row);
    const showResponse = inquiry || isResponseNote(row);

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography
                    variant="body2"
                    fontWeight={indent || isNote ? 400 : 600}
                    sx={{pl: indent ? 1 : 0}}
                >
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                <Typography
                    variant="body2"
                    sx={{
                        lineHeight: 1.4,
                        pl: indent ? 1.5 : 0,
                        fontStyle: isNote ? 'italic' : 'normal',
                        color: isNote ? 'text.secondary' : 'text.primary',
                        fontWeight: isHeading ? 600 : undefined,
                    }}
                >
                    {row.text}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                {inquiry ? (
                    <NativeYesNo
                        value={yesNo}
                        onChange={(v) => onChange(`${row.id}.yesNo`, v)}
                    />
                ) : null}
            </TableCell>
            <TableCell sx={bodySx}>
                {showResponse ? (
                    <NativeInput
                        value={response}
                        onChange={(v) => onChange(`${row.id}.response`, v)}
                    />
                ) : null}
            </TableCell>
            <TableCell sx={bodySx}>
                {inquiry ? (
                    <NativeInput
                        value={initials}
                        onChange={(v) => onChange(`${row.id}.initials`, v)}
                        align="center"
                    />
                ) : null}
            </TableCell>
            <TableCell sx={bodySx}>
                {inquiry ? (
                    <NativeInput
                        value={wpRef}
                        onChange={(v) => onChange(`${row.id}.wpRef`, v)}
                        align="center"
                    />
                ) : null}
            </TableCell>
        </TableRow>
    );
});

/**
 * Excel C1a Engagement Acceptance — Yes/No · Response · Initials · W/P Ref.
 * Uses native inputs (not MUI TextField) so ~350 cells stay responsive.
 */
export default function C1aAcceptanceView({header, values, onChange, content}: C1aAcceptanceViewProps) {
    const copy = content ?? C1A_CONTENT;
    const getResponse = useCallback(
        (rowId: string) => values[`${rowId}.response`] ?? values[`${rowId}.comments`] ?? '',
        [values],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C1a"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {copy.title}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: 'block', textAlign: 'center', mb: 1, mt: -1}}
            >
                {copy.subtitle}
            </Typography>
            <Typography variant="body2" sx={{mb: 1.5, lineHeight: 1.45}}>
                {copy.intro}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
                    gap: 1,
                    mb: 1.5,
                    maxWidth: 960,
                }}
            >
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <Typography variant="body2" sx={{minWidth: 180, flexShrink: 0}}>
                        {copy.meta.clientLabel}:
                    </Typography>
                    <NativeInput
                        value={values.prospectiveClientName ?? values.client ?? header.client ?? ''}
                        onChange={(v) => onChange('prospectiveClientName', v)}
                    />
                </Box>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <Typography variant="body2" sx={{minWidth: 160, flexShrink: 0}}>
                        {copy.meta.assignmentLabel}:
                    </Typography>
                    <NativeInput
                        value={values.natureOfAssignment ?? 'Audit'}
                        onChange={(v) => onChange('natureOfAssignment', v)}
                    />
                </Box>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                    <Typography variant="body2" sx={{minWidth: 180, flexShrink: 0}}>
                        {copy.meta.periodLabel}:
                    </Typography>
                    <NativeInput
                        value={values.periodEnding ?? values.yearEnd ?? header.yearEnd ?? ''}
                        onChange={(v) => onChange('periodEnding', v)}
                    />
                </Box>
                <Box sx={{display: 'flex', gap: 1, alignItems: 'center', gridColumn: {sm: '1 / -1'}}}>
                    <Typography variant="body2" sx={{minWidth: 180, flexShrink: 0}}>
                        {copy.meta.additionalLabel}:
                    </Typography>
                    <NativeInput
                        value={values.additionalAssignments ?? ''}
                        onChange={(v) => onChange('additionalAssignments', v)}
                    />
                </Box>
            </Box>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 920, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 40}}/>
                        <col/>
                        <col style={{width: 88}}/>
                        <col style={{width: 220}}/>
                        <col style={{width: 72}}/>
                        <col style={{width: 80}}/>
                    </colgroup>
                    <TableBody>
                        {copy.rows.map((row) => {
                            if (row.kind === 'section') {
                                // Excel: plain section title, then black column headers.
                                // Exception r57: Col A of the black band is the section title itself.
                                const isProhibitionsHeader = row.id === 'r57';
                                const inquiryLabel = isProhibitionsHeader
                                    ? row.text
                                    : 'Line of Inquiry';
                                return (
                                    <React.Fragment key={row.id}>
                                        {!isProhibitionsHeader ? (
                                            <TableRow>
                                                <TableCell sx={{...bodySx}} colSpan={6}>
                                                    <Typography variant="body2" fontWeight={700} sx={{py: 0.25}}>
                                                        {row.text}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                        <C1aSectionColumnHeader inquiryLabel={inquiryLabel}/>
                                    </React.Fragment>
                                );
                            }

                            if (row.kind === 'decision') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <Typography variant="body2" sx={{lineHeight: 1.4}}>
                                                In my opinion, we should
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                                <NativeSelect
                                                    value={values.engagementDecision ?? ''}
                                                    onChange={(v) => onChange('engagementDecision', v)}
                                                    sx={{minWidth: 140, width: 'auto'}}
                                                >
                                                    <option value="">—</option>
                                                    {copy.decisionOptions.map((opt) => (
                                                        <option key={opt} value={opt}>{opt}</option>
                                                    ))}
                                                </NativeSelect>
                                                <Typography variant="body2">this engagement.</Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            if (row.kind === 'signature') {
                                const nameKey = `${row.id}.name`;
                                const dateKey = `${row.id}.date`;
                                const defaultName =
                                    row.id === 'r172'
                                        ? values[nameKey] || values.partnerName || header.partnerName || ''
                                        : values[nameKey] || '';
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <Typography variant="body2" sx={{lineHeight: 1.4}}>
                                                {row.text}:
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <NativeInput
                                                value={defaultName}
                                                onChange={(v) => onChange(nameKey, v)}
                                            />
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <Box sx={{display: 'flex', gap: 0.75, alignItems: 'center'}}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Date
                                                </Typography>
                                                <NativeInput
                                                    value={values[dateKey] ?? ''}
                                                    onChange={(v) => onChange(dateKey, v)}
                                                    placeholder="dd/mm/yyyy"
                                                />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            if (row.id === 'r164') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={2}>
                                            <Typography variant="body2" sx={{lineHeight: 1.4}}>
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            <NativeSelect
                                                value={values.clientRiskRating ?? ''}
                                                onChange={(v) => onChange('clientRiskRating', v)}
                                            >
                                                <option value="">—</option>
                                                {copy.riskOptions.map((opt) => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </NativeSelect>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            if (row.kind === 'note' && !isResponseNote(row)) {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={{...bodySx, py: 0.25}} colSpan={6}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    lineHeight: 1.4,
                                                    fontStyle: 'italic',
                                                    color: 'text.secondary',
                                                    pl: 0.5,
                                                }}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            return (
                                <InquiryRow
                                    key={row.id}
                                    row={row}
                                    yesNo={values[`${row.id}.yesNo`] ?? ''}
                                    response={getResponse(row.id)}
                                    initials={values[`${row.id}.initials`] ?? ''}
                                    wpRef={values[`${row.id}.wpRef`] ?? ''}
                                    onChange={onChange}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
